import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="w-full py-4 px-6 bg-white shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2">
            <img src="/bloodResults.png" alt="Blood Results Logo" className="h-8 w-auto" />
            <span className="text-lg font-semibold text-gray-900">Blood Results</span>
          </Link>
        </div>
      </header>

      {/* Brand Banner */}
      <div className="w-full py-6 bg-white flex justify-center border-t border-gray-200">
        <img
          src="/blood-results-brand.svg"
          alt="Blood Results Brand"
          className="h-20 w-auto"
        />
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-500 mb-8">
            Last updated: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>

          <div className="space-y-8">
            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed">
                KNX Associates Ltd (trading as "Blood Results") ("we", "us", or "our") is committed to protecting
                your privacy and ensuring the security of your personal and health data. This Privacy Policy explains
                how we collect, use, disclose, and safeguard your information when you use our blood test results
                management platform.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>Data Controller:</strong> KNX Associates Ltd, registered in England and Wales.<br />
                <strong>Contact:</strong> privacy@knx-associates.co.uk
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. Information We Collect</h2>

              <div className="space-y-4 ml-4">
                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2.1 Personal Information</h3>
                  <p className="text-gray-700 leading-relaxed mb-2">We collect the following personal information:</p>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1">
                    <li>Name and contact details (email address)</li>
                    <li>Account credentials (username and encrypted password)</li>
                    <li>Date of birth and gender</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2.2 Health Information</h3>
                  <p className="text-gray-700 leading-relaxed mb-2">We process sensitive health data including:</p>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1">
                    <li>Blood test results uploaded via PDF documents</li>
                    <li>Laboratory test identifiers and sample dates</li>
                    <li>Test result values, units, and reference ranges</li>
                    <li>Historical health data for trend analysis</li>
                  </ul>
                </div>

                <div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2.3 Technical Information</h3>
                  <p className="text-gray-700 leading-relaxed mb-2">We automatically collect:</p>
                  <ul className="list-disc ml-6 text-gray-700 space-y-1">
                    <li>IP address and browser type</li>
                    <li>Device information and operating system</li>
                    <li>Usage data and access times</li>
                    <li>Cookies and similar tracking technologies</li>
                  </ul>
                </div>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Legal Basis for Processing</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We process your health data under the following legal bases:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-2">
                <li><strong>Explicit Consent:</strong> You provide explicit consent when uploading health data to our platform</li>
                <li><strong>Contract Performance:</strong> Processing is necessary to provide our services to you</li>
                <li><strong>Legitimate Interests:</strong> To improve our services and ensure platform security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-2">We use your information to:</p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                <li>Parse and store your blood test results securely</li>
                <li>Generate visualizations and trend analysis of your health data</li>
                <li>Provide customer support and respond to inquiries</li>
                <li>Maintain and improve our platform's functionality</li>
                <li>Ensure security and prevent fraud</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Sharing and Disclosure</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We do not sell, trade, or rent your personal or health information. We may share your data only in
                the following circumstances:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-2">
                <li><strong>With Your Consent:</strong> When you explicitly authorize data export (e.g., to Apple Health)</li>
                <li><strong>Service Providers:</strong> Trusted third parties who assist us (e.g., hosting providers, database services) under strict confidentiality agreements</li>
                <li><strong>Legal Requirements:</strong> When required by law, court order, or to protect our rights</li>
                <li><strong>Business Transfers:</strong> In connection with any merger, acquisition, or sale of assets</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                We implement industry-standard security measures to protect your data:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                <li>SSL/TLS encryption for data in transit</li>
                <li>Encrypted storage of health data at rest</li>
                <li>Regular security audits and vulnerability assessments</li>
                <li>Access controls and authentication mechanisms</li>
                <li>Secure backup and disaster recovery procedures</li>
                <li>Staff training on data protection and confidentiality</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed">
                We retain your personal and health data for as long as your account is active or as needed to provide
                our services. You may request deletion of your data at any time. After account deletion, we will
                permanently delete your data within 30 days, except where retention is required by law.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Your Rights (UK GDPR)</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                Under UK GDPR, you have the following rights regarding your data:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                <li><strong>Right of Access:</strong> Request a copy of your personal data</li>
                <li><strong>Right to Rectification:</strong> Correct inaccurate or incomplete data</li>
                <li><strong>Right to Erasure:</strong> Request deletion of your data ("right to be forgotten")</li>
                <li><strong>Right to Restrict Processing:</strong> Limit how we use your data</li>
                <li><strong>Right to Data Portability:</strong> Receive your data in a structured, machine-readable format</li>
                <li><strong>Right to Object:</strong> Object to processing based on legitimate interests</li>
                <li><strong>Right to Withdraw Consent:</strong> Withdraw consent for health data processing at any time</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, contact us at: <a href="mailto:privacy@knx-associates.co.uk" className="text-blue-600 hover:underline">privacy@knx-associates.co.uk</a>
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your data is primarily stored and processed within the UK/EEA. If we transfer data outside these
                regions, we ensure appropriate safeguards are in place, such as Standard Contractual Clauses or
                adequacy decisions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Our service is not intended for children under 16. We do not knowingly collect personal information
                from children. If you believe we have collected data from a child, please contact us immediately.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">11. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                Our platform may integrate with third-party services:
              </p>
              <ul className="list-disc ml-6 text-gray-700 space-y-1">
                <li><strong>Apple Health:</strong> Data export requires your explicit authorization</li>
                <li><strong>Cloud Storage:</strong> Secure hosting infrastructure providers</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                These services have their own privacy policies, and we encourage you to review them.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of any material changes by
                posting the new policy on this page and updating the "Last updated" date. Continued use of our
                services after changes constitutes acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-gray-900 mb-4">13. Contact Us and Complaints</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions about this Privacy Policy or wish to exercise your rights, contact us:
              </p>
              <ul className="space-y-1 text-gray-700 mb-4">
                <li><strong>Company:</strong> KNX Associates Ltd</li>
                <li><strong>Trading as:</strong> Blood Results</li>
                <li><strong>Email:</strong> <a href="mailto:privacy@knx-associates.co.uk" className="text-blue-600 hover:underline">privacy@knx-associates.co.uk</a></li>
                <li><strong>Data Protection Officer:</strong> <a href="mailto:dpo@knx-associates.co.uk" className="text-blue-600 hover:underline">dpo@knx-associates.co.uk</a></li>
              </ul>
              <p className="text-gray-700 leading-relaxed">
                You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) if you
                believe we have not handled your data properly: <a href="https://ico.org.uk" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">www.ico.org.uk</a>
              </p>
            </section>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <Link to="/" className="text-blue-600 hover:text-blue-700">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="w-full py-6 px-6 bg-white border-t border-gray-200 mt-12">
        <div className="max-w-7xl mx-auto text-center text-sm text-gray-500">
          <p>&copy; 2025 Blood-Results.co.uk A KNX-Associates Ltd Brand. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
